import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { useLanguage } from '../../contexts/LanguageContext';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );
}

function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'default',
  showCloseButton = true,
  closeOnOverlayClick = true,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) {
  const { t } = useLanguage();
  const titleId = useId();
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const reducedMotion = useReducedMotion();
  onCloseRef.current = onClose;

  const sizeClasses = {
    small: 'modal-sheet-sm',
    default: 'modal-sheet',
    large: 'modal-sheet-lg',
    xl: 'modal-sheet-xl',
    prospect: 'modal-sheet-prospect',
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusDialog = () => {
      const node = dialogRef.current;
      if (!node) return;
      const active = document.activeElement;
      if (active && node.contains(active) && active !== node) return;
      const focusables = getFocusableElements(node);
      const marked = focusables.find((el) => el.getAttribute('data-modal-initial-focus') === 'true');
      const firstField = focusables.find((el) => (
        el.matches('input:not([type="hidden"]):not([type="file"]), textarea, select')
      ));
      const target = marked || firstField || focusables[0] || node;
      target.focus?.();
    };

    const frame = requestAnimationFrame(focusDialog);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const node = dialogRef.current;
      if (!node) return;
      const focusables = getFocusableElements(node);
      if (focusables.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === 'function') {
        previouslyFocusedRef.current = null;
        previous.focus();
      }
    };
  }, [isOpen]);

  if (typeof document === 'undefined') return null;

  const labelledBy = ariaLabelledBy || (title ? titleId : undefined);

  const sheetMotion = reducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, scale: 0.95, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 20 },
      };

  const content = (
    <AnimatePresence mode="wait">
      {isOpen ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center safe-x p-4 md:p-6">
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0.01 : 0.2 }}
            className="absolute inset-0 bg-secondary-900/45 backdrop-blur-md"
            onClick={handleOverlayClick}
            aria-hidden="true"
          />

          <motion.div
            key="modal"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-describedby={ariaDescribedBy}
            tabIndex={-1}
            {...sheetMotion}
            transition={
              reducedMotion
                ? { duration: 0.01 }
                : { type: 'spring', stiffness: 420, damping: 32 }
            }
            className={`relative ${sizeClasses[size]} max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem)] overflow-hidden outline-none sm:max-h-[90vh]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-premium shadow-premium hover:scale-100 hover:shadow-premium flex max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem)] flex-col sm:max-h-[90vh]">
              {(title || showCloseButton) && (
                <div className="relative flex shrink-0 items-start justify-between gap-3 border-b border-secondary-200/60 bg-gradient-to-r from-orange-50/40 via-white/50 to-amber-50/30 p-4 sm:items-center sm:p-6    ">
                  {title ? (
                    <h3
                      id={titleId}
                      className="min-w-0 flex-1 pr-2 text-base font-bold tracking-tight text-secondary-900  sm:text-lg md:text-xl"
                    >
                      {title}
                    </h3>
                  ) : null}

                  {showCloseButton ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="small"
                      onClick={onClose}
                      aria-label={t.common?.close || 'Close'}
                      className="max-sm:touch-target shrink-0 !px-2 !py-2 text-secondary-400 hover:text-secondary-600 "
                      icon={XMarkIcon}
                    />
                  ) : null}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 max-sm:safe-bottom">
                {children}
              </div>

              {footer || null}
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}

export default Modal;
